import { z } from 'zod';

import {
  evaluateFieldRequired,
  type ValidatedField,
} from '@/utils/validation/schemaHelpers';

import type { ContractValidationScope } from '@/utils/validation/contractValidation';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';

export type FormSchema = {
  zodSchema: z.ZodType;
  fieldNames: string[];
  hasField: (name: string | (string | number)[]) => boolean;
  isFieldRequired: (
    name: string | (string | number)[],
    values?: Record<string, unknown>,
    scope?: ValidationScope,
    contractScope?: ContractValidationScope,
  ) => boolean;
};

/** Map a Form.Item name (including nested paths) to a top-level schema field key. */
export function resolveSchemaFieldName(
  name: string | (string | number)[],
  fieldNames: readonly string[],
): string | null {
  if (typeof name === 'string') {
    return fieldNames.includes(name) ? name : null;
  }
  for (const segment of name) {
    if (typeof segment === 'string' && fieldNames.includes(segment)) {
      return segment;
    }
  }
  return null;
}

export function buildFormSchema(
  fields: Record<string, ValidatedField>,
  refinements?: (schema: z.ZodObject<z.ZodRawShape>) => z.ZodType,
): FormSchema {
  const shape = Object.fromEntries(
    Object.entries(fields).map(([key, field]) => [key, field.schema]),
  );
  const baseSchema = z.object(shape).loose();
  const zodSchema = refinements ? refinements(baseSchema) : baseSchema;

  const fieldNames = Object.keys(fields);

  return {
    zodSchema,
    fieldNames,
    hasField: (name) => resolveSchemaFieldName(name, fieldNames) != null,
    isFieldRequired: (name, values = {}, scope = 'save', contractScope) => {
      const key = resolveSchemaFieldName(name, fieldNames);
      const field = key != null ? fields[key] : undefined;
      if (!field) {
        return false;
      }
      return evaluateFieldRequired(
        field.isRequired,
        values,
        scope,
        contractScope,
      );
    },
  };
}

export function getFieldNames(
  fields: Record<string, ValidatedField>,
): string[] {
  return Object.keys(fields);
}
